import { Mask, PixelDesign, Point, SizePx } from '@/types';
import { assertDefined } from '@/utils/assertDefined';
import { requestBlob } from '@/utils/http/requestBlob';
import { loadImage } from '@/utils/image/loadImage';
import { trackError } from '@/utils/raygun';
import { getImageObjectUrl, setImageObjectUrl } from '@/utils/upload/imageObjectUrls';
import { getConfigurationFilterObjectUrl } from '@/features/filters/getConfigurationFilterObjectUrl';
import { getConfigurationMaskId } from './getConfigurationMaskId';
import { getMaskedCanvas } from './getMaskedCanvas';
import { getScaleAndOffsetForMask } from './getScaleAndOffsetForMask';
import { initDesignImage } from './initDesignImage';
import { initMaskImage } from './initMaskImage';

export async function getMaskedDesignPreview(
  mask: Mask,
  configurationId: number,
  design: PixelDesign,
  scale?: Point,
  offset?: Point,
  ignoreFilter?: boolean
): Promise<[SizePx, Point, Point]> {
  const configurationObjectUrl =
    (!ignoreFilter && getConfigurationFilterObjectUrl(configurationId)) ||
    getImageObjectUrl(design.id);

  const [designImage, maskImage] = await Promise.all([
    configurationObjectUrl
      ? loadImage(configurationObjectUrl, configurationObjectUrl.endsWith('.png'))
      : initDesignImage(design),
    initMaskImage(mask),
  ]);

  const { size: designSize } = design;

  if (!scale || !offset) {
    [scale, offset] = getScaleAndOffsetForMask(maskImage, designSize);
  }

  /**
   * Get the masked image from a intermediate canvas object. Image is trimmed to mask dimensions
   */
  const ctx = assertDefined(getMaskedCanvas(designImage, maskImage, scale, offset, null, true));
  /**
   * The scaling factor is needed to calculate the actual size of the mask needed.
   */
  const processedSize: SizePx = {
    width: designSize.width * scale.x,
    height: designSize.height * scale.y,
    unit: 'px',
  };

  let imageBlob = await new Promise<Blob | null>((resolve) => ctx.canvas.toBlob(resolve));
  if (!imageBlob) {
    trackError('getMaskedDesignPreview failed', {
      design: design,
      mask: mask.id,
      scale,
      offset,
    });
    imageBlob = await requestBlob(designImage.src);
  }

  setImageObjectUrl(getConfigurationMaskId(configurationId), URL.createObjectURL(imageBlob));

  return [processedSize, scale, offset];
}
